/**
 * Preferences Service Client
 *
 * Provides a centralized client for all user preferences operations.
 * Uses flat user_preferences table columns via the settings microservice.
 */

import { API_ENDPOINTS, getAuthHeader } from "@/config/api";
import { getAuthToken } from "./assets/assetHelpers";
import { authService } from "./auth/index";
import {
  isPreferencesApiCircuitOpen,
  recordPreferencesServerFailure,
} from "@/utils/preferencesApiCircuit";
import { CLEARED_PAN_PRIVACY_SETTINGS } from "@/utils/panStorage";
import { PREFERENCES_SAVE_DEBOUNCE_MS } from "@/constants/preferencesSaveDebounce";

export { PREFERENCES_SAVE_DEBOUNCE_MS };

/** Flat row shape returned by settings-service (matches DB columns). */
export interface UserPreferencesRow {
  user_id?: string;
  theme?: string;
  currency?: string;
  language?: string;
  timezone?: string;
  investment_preferences?: {
    categories?: Record<string, boolean>;
  };
  notification_settings?: Record<string, unknown>;
  privacy_settings?: Record<string, unknown>;
  encrypted_pan?: string | null;
  pan_iv?: string | null;
  pan_hash?: string | null;
  created_at?: string;
  updated_at?: string;
}

export type PreferencesPatch = Partial<
  Pick<
    UserPreferencesRow,
    | "theme"
    | "currency"
    | "language"
    | "timezone"
    | "investment_preferences"
    | "notification_settings"
    | "privacy_settings"
    | "encrypted_pan"
    | "pan_iv"
    | "pan_hash"
  >
>;

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let pendingPatch: PreferencesPatch | null = null;
let pendingUserId: string | null = null;
let pendingSavePromise: Promise<UserPreferencesRow> | null = null;
let pendingSaveResolve: ((value: UserPreferencesRow) => void) | null = null;
let pendingSaveReject: ((reason: Error) => void) | null = null;

/**
 * Make authenticated API call with retry logic for rate limiting
 */
async function apiCall<T>(
  endpoint: string,
  options: globalThis.RequestInit = {},
  retryCount = 0
): Promise<T> {
  if (isPreferencesApiCircuitOpen()) {
    throw new Error("Preferences service temporarily unavailable. Please try again shortly.");
  }

  const token = await getAuthToken();

  const isStateChangingOperation = ["POST", "PUT", "DELETE", "PATCH"].includes(
    options.method || "GET"
  );
  const headers = isStateChangingOperation
    ? await authService.getAuthHeaders()
    : {
        ...getAuthHeader(token || undefined),
        ...options.headers,
      };

  const response = await fetch(endpoint, {
    ...options,
    headers,
    credentials: "include",
  });

  if (response.status === 429) {
    const maxRetries = 2;
    if (retryCount < maxRetries) {
      const retryAfter = response.headers.get("Retry-After");
      let delay: number;

      if (retryAfter) {
        const retrySeconds = Number.parseInt(retryAfter, 10);
        delay = Math.min(retrySeconds * 1000, 60000);
      } else {
        delay = Math.min(1000 * 2 ** retryCount, 10000);
      }

      console.warn(
        `[PreferencesService] Rate limited (429), retrying after ${Math.round(delay / 1000)}s (attempt ${retryCount + 1}/${maxRetries})`
      );

      await new Promise((resolve) => setTimeout(resolve, delay));
      return apiCall<T>(endpoint, options, retryCount + 1);
    }
    const errorData = await response.json().catch(() => ({ error: "Rate limit exceeded" }));
    throw new Error(errorData.error || `HTTP 429: Too Many Requests (max retries exceeded)`);
  }

  if (!response.ok) {
    recordPreferencesServerFailure(response.status);
    const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(errorData.error || `API call failed: ${response.statusText}`);
  }

  return response.json();
}

/** Flush debounced save immediately (for tests and reset). */
export function flushPendingPreferencesSave(): Promise<UserPreferencesRow | null> {
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  if (pendingSavePromise !== null && pendingUserId && pendingPatch) {
    const userId = pendingUserId;
    const patch = pendingPatch;
    pendingPatch = null;
    pendingUserId = null;
    return preferencesService.patchPreferencesImmediate(userId, patch);
  }
  return Promise.resolve(null);
}

/** Reset debounce state (for tests). */
export function resetPreferencesSaveDebounceForTests(): void {
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  pendingPatch = null;
  pendingUserId = null;
  pendingSavePromise = null;
  pendingSaveResolve = null;
  pendingSaveReject = null;
}

/** Matches `create_user_preferences.sql` notification_settings defaults. */
export const DEFAULT_NOTIFICATION_SETTINGS = {
  email: true,
  push: false,
  sms: false,
  transactionAlerts: true,
  goalReminders: true,
  marketUpdates: false,
} as const;

export type NotificationSettings = typeof DEFAULT_NOTIFICATION_SETTINGS;

/** Primary notification toggle — email channel drives `notification_enabled` in UI. */
export function isNotificationEnabled(settings?: Record<string, unknown> | null): boolean {
  if (!settings) {
    return DEFAULT_NOTIFICATION_SETTINGS.email;
  }
  if (typeof settings.email === "boolean") {
    return settings.email;
  }
  return true;
}

export const DEFAULT_INVESTMENT_PREFERENCES = {
  categories: {
    stocks: true,
    mutualFunds: true,
    bonds: true,
    realEstate: false,
    crypto: false,
    commodities: false,
    fixedDeposits: true,
    gold: true,
    pf: true,
    ppf: true,
    vpf: true,
    insurance: true,
    bankBalance: true,
  },
};

export const preferencesService = {
  async fetchPreferences(userId: string): Promise<UserPreferencesRow | null> {
    try {
      const endpoint = `${API_ENDPOINTS.SETTINGS.PREFERENCES}/${userId}`;
      const preferences = await apiCall<UserPreferencesRow | null>(endpoint, {
        method: "GET",
      });
      return preferences;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      if (errorMessage.includes("404") || errorMessage.includes("not found")) {
        return null;
      }
      console.error("[PreferencesService] fetchPreferences failed:", error);
      throw error;
    }
  },

  /** Immediate partial update — one HTTP round trip, server merges with existing row. */
  async patchPreferencesImmediate(
    userId: string,
    patch: PreferencesPatch
  ): Promise<UserPreferencesRow> {
    const endpoint = `${API_ENDPOINTS.SETTINGS.PREFERENCES}/${userId}`;
    const preferences = await apiCall<UserPreferencesRow>(endpoint, {
      method: "PUT",
      body: JSON.stringify(patch),
    });
    return preferences;
  },

  /**
   * Debounced partial update — batches rapid toggles/slider moves into one PUT.
   * Returns a promise that resolves when the debounced save completes.
   */
  patchPreferences(userId: string, patch: PreferencesPatch): Promise<UserPreferencesRow> {
    pendingUserId = userId;
    pendingPatch = {
      ...pendingPatch,
      ...patch,
      investment_preferences: patch.investment_preferences
        ? {
            ...pendingPatch?.investment_preferences,
            ...patch.investment_preferences,
            categories: {
              ...pendingPatch?.investment_preferences?.categories,
              ...patch.investment_preferences.categories,
            },
          }
        : pendingPatch?.investment_preferences,
    };

    if (!pendingSavePromise) {
      pendingSavePromise = new Promise((resolve, reject) => {
        pendingSaveResolve = resolve;
        pendingSaveReject = reject;
      });
    }

    if (saveTimer) {
      clearTimeout(saveTimer);
    }

    saveTimer = setTimeout(async () => {
      const uid = pendingUserId;
      const body = pendingPatch;
      const resolve = pendingSaveResolve;
      const reject = pendingSaveReject;

      pendingUserId = null;
      pendingPatch = null;
      pendingSavePromise = null;
      pendingSaveResolve = null;
      pendingSaveReject = null;
      saveTimer = null;

      if (!uid || !body) {
        reject?.(new Error("No pending preferences to save"));
        return;
      }

      try {
        const result = await preferencesService.patchPreferencesImmediate(uid, body);
        resolve?.(result);
      } catch (error) {
        reject?.(error instanceof Error ? error : new Error(String(error)));
      }
    }, PREFERENCES_SAVE_DEBOUNCE_MS);

    return pendingSavePromise;
  },

  async getRegionalPreferences(
    userId: string
  ): Promise<{ currency: string; language: string; timezone: string }> {
    const preferences = await this.fetchPreferences(userId);
    // India-only: currency and language are fixed; timezone may still vary.
    return {
      currency: "INR",
      language: "en",
      timezone: preferences?.timezone || "Asia/Kolkata",
    };
  },

  async updateRegionalPreferences(
    userId: string,
    regionalPrefs: { currency?: string; language?: string; timezone?: string }
  ): Promise<UserPreferencesRow> {
    const result = await this.patchPreferencesImmediate(userId, {
      currency: "INR",
      language: "en",
      timezone: regionalPrefs.timezone || "Asia/Kolkata",
    });
    return result;
  },

  async getFinancePreferences(
    userId: string
  ): Promise<UserPreferencesRow["investment_preferences"]> {
    const preferences = await this.fetchPreferences(userId);
    return preferences?.investment_preferences || DEFAULT_INVESTMENT_PREFERENCES;
  },

  async updateFinancePreferences(
    userId: string,
    investmentPrefs: UserPreferencesRow["investment_preferences"]
  ): Promise<UserPreferencesRow> {
    const result = await this.patchPreferences(userId, { investment_preferences: investmentPrefs });
    return result;
  },

  async getNotificationPreferences(userId: string): Promise<NotificationSettings> {
    const preferences = await this.fetchPreferences(userId);
    const stored = preferences?.notification_settings;
    if (!stored || typeof stored !== "object") {
      return { ...DEFAULT_NOTIFICATION_SETTINGS };
    }
    return {
      email: typeof stored.email === "boolean" ? stored.email : DEFAULT_NOTIFICATION_SETTINGS.email,
      push: typeof stored.push === "boolean" ? stored.push : DEFAULT_NOTIFICATION_SETTINGS.push,
      sms: typeof stored.sms === "boolean" ? stored.sms : DEFAULT_NOTIFICATION_SETTINGS.sms,
      transactionAlerts:
        typeof stored.transactionAlerts === "boolean"
          ? stored.transactionAlerts
          : DEFAULT_NOTIFICATION_SETTINGS.transactionAlerts,
      goalReminders:
        typeof stored.goalReminders === "boolean"
          ? stored.goalReminders
          : DEFAULT_NOTIFICATION_SETTINGS.goalReminders,
      marketUpdates:
        typeof stored.marketUpdates === "boolean"
          ? stored.marketUpdates
          : DEFAULT_NOTIFICATION_SETTINGS.marketUpdates,
    };
  },

  async getAppearancePreferences(userId: string): Promise<{ theme: string }> {
    const preferences = await this.fetchPreferences(userId);
    return { theme: preferences?.theme || "light" };
  },

  async updateAppearancePreferences(
    userId: string,
    appearancePrefs: { theme?: string }
  ): Promise<UserPreferencesRow> {
    const result = await this.patchPreferencesImmediate(userId, { theme: appearancePrefs.theme });
    return result;
  },

  async resetToDefaults(userId: string): Promise<UserPreferencesRow> {
    await flushPendingPreferencesSave();
    return this.patchPreferencesImmediate(userId, {
      theme: "light",
      currency: "INR",
      language: "en",
      timezone: "Asia/Kolkata",
      investment_preferences: DEFAULT_INVESTMENT_PREFERENCES,
      notification_settings: { ...DEFAULT_NOTIFICATION_SETTINGS },
      encrypted_pan: null,
      pan_iv: null,
      pan_hash: null,
      privacy_settings: { ...CLEARED_PAN_PRIVACY_SETTINGS },
    });
  },

  async savePanDetails(
    userId: string,
    panData: {
      encrypted_pan: string;
      pan_iv: string;
      pan_hash: string;
      pan_details_encrypted?: { cipher: string; iv: string } | null;
      masked_pan: string;
    }
  ): Promise<UserPreferencesRow> {
    const privacySettings: Record<string, unknown> = {
      masked_pan: panData.masked_pan,
      pan_details: null,
    };

    if (panData.pan_details_encrypted) {
      privacySettings.encrypted_pan_details = panData.pan_details_encrypted.cipher;
      privacySettings.pan_details_iv = panData.pan_details_encrypted.iv;
    }

    const result = await this.patchPreferencesImmediate(userId, {
      encrypted_pan: panData.encrypted_pan,
      pan_iv: panData.pan_iv,
      pan_hash: panData.pan_hash,
      privacy_settings: privacySettings,
    });
    return result;
  },

  async fetchPrivacyConsent(userId: string): Promise<Record<string, unknown>[]> {
    try {
      const consents = await apiCall<Record<string, unknown>[]>(
        `${API_ENDPOINTS.SETTINGS.PRIVACY_CONSENT}/${userId}`,
        { method: "GET" }
      );
      return consents || [];
    } catch (error) {
      console.error("[PreferencesService] fetchPrivacyConsent failed:", error);
      throw error;
    }
  },

  async createPrivacyConsent(consent: {
    user_id: string;
    consent_date: string;
    consent_type?: string;
    ip_address?: string;
    location?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  }): Promise<Record<string, unknown>> {
    try {
      return await apiCall<Record<string, unknown>>(API_ENDPOINTS.SETTINGS.PRIVACY_CONSENT, {
        method: "POST",
        body: JSON.stringify(consent),
      });
    } catch (error) {
      console.error("[PreferencesService] createPrivacyConsent failed:", error);
      throw error;
    }
  },
};

/** @deprecated Use UserPreferencesRow — kept for gradual migration */
export type UserPreferences = UserPreferencesRow;
